import { useCasesStore, type Case } from '~/stores/cases'
import { useClientsStore, type Client } from '~/stores/clients'

/**
 * Shared state for the chat scope control (§6b): open/closed, plus the two lists the ladder is built
 * from.
 *
 * The control is split across two components — `ChatScopeBar` (the row attached to the composer card)
 * and `ChatScopePanel` (the browsing body it expands into) — so the flag and the fetch live here
 * rather than being threaded as props. `useState` also means the lists are fetched **once per
 * session** instead of once per chat surface: the bar needs them to decide whether to render at all,
 * and the panel needs them to build the columns.
 *
 * **CLIENTS ARE FETCHED, NOT DERIVED FROM THE CASES.** Column 1 used to be built by grouping
 * `listCases()` on `client_id` — no second request, and a client with no cases was deliberately
 * absent because scoping to it yields an empty document set. That reasoning was overruled
 * (AlexAmin/marcus#85): scoping exists to keep the conversation in a client's context, not because
 * there are files to read, and the picker disagreeing with the document browser — which has always
 * read `/api/clients` — is what made a client created minutes earlier invisible to the chat. The two
 * surfaces now read the same list.
 */
export function useScopePanel() {
  const open = useState('chat-scope-panel-open', () => false)
  const clients = useState<Client[]>('chat-scope-clients', () => [])
  const cases = useState<Case[]>('chat-scope-cases', () => [])
  /** True only after a SUCCESSFUL load of BOTH lists — empty then means "this org has neither". */
  const loaded = useState('chat-scope-loaded', () => false)
  const loading = useState('chat-scope-loading', () => false)

  /**
   * Returns whether the lists are trustworthy, which callers must respect before treating an empty
   * `clients`/`cases` as "none exist".
   *
   * Waits for Firebase to restore the session first. Without that, this fires on mount before
   * `getToken()` can return anything, the requests go out unauthenticated, and the 401 is swallowed
   * into an empty list — indistinguishable from an org with nothing in it. Failure also leaves
   * `loaded` false so a transient error can retry instead of blanking the control for the session.
   *
   * **Both lists load or neither does.** `loaded` is a single flag standing for both, so a
   * half-populated pair must never set it: a clients fetch that succeeded beside a cases fetch that
   * 401'd would publish an allow-list missing every case id, and `applyRememberedScope` would read
   * that as licence to forget a live case scope. `Promise.all` rejects on the first failure, which is
   * exactly the semantics wanted here.
   *
   * **A concurrent caller AWAITS the in-flight load rather than being told `false`.** This used to
   * return false the moment a load was already running, which conflates "not trustworthy" with
   * "not ready yet" — and every caller reads false as "give up". `ChatScopeBar` starts this fetch
   * from the app shell on every page, so a resolver mounting alongside it (`useRouteScope`,
   * `useRestoreScope`, `useOpenScope`) lost the race and silently resolved nothing. That is how a
   * `/app/cases/:id/workflow/:id` link failed to scope to its own case (MAR-176).
   *
   * The promise hangs off `useNuxtApp()`, which is per-request — a module-level variable would be
   * shared across SSR requests, i.e. one user's in-flight lists handed to another.
   */
  const nuxtApp = useNuxtApp() as unknown as { _scopeListsInflight?: Promise<boolean> }

  async function ensureScopeLists(): Promise<boolean> {
    if (loaded.value) return true
    if (nuxtApp._scopeListsInflight) return nuxtApp._scopeListsInflight

    loading.value = true
    const inflight = (async () => {
      try {
        await useAuth().waitForAuth()
        const [clientRows, caseRows] = await Promise.all([
          useClientsStore().listClients(),
          useCasesStore().listCases(),
        ])
        clients.value = clientRows
        cases.value = caseRows
        loaded.value = true
        return true
      } catch {
        return false
      } finally {
        loading.value = false
        nuxtApp._scopeListsInflight = undefined
      }
    })()

    nuxtApp._scopeListsInflight = inflight
    return inflight
  }

  function toggle() {
    open.value = !open.value
  }

  function close() {
    open.value = false
  }

  return { open, toggle, close, clients, cases, loaded, loading, ensureScopeLists }
}
